/**
 * Drawdown Distribution Mock
 *
 * Histogram showing the distribution of maximum drawdowns
 * across Monte Carlo simulations, highlighting the P95
 * worst-case drawdown vs the backtest drawdown.
 */

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  Cell,
} from 'recharts';

const bins = [
  { range: '-5%', count: 15, midpoint: -5 },
  { range: '-10%', count: 68, midpoint: -10 },
  { range: '-15%', count: 145, midpoint: -15 },
  { range: '-20%', count: 225, midpoint: -20 },
  { range: '-25%', count: 210, midpoint: -25 },
  { range: '-30%', count: 155, midpoint: -30 },
  { range: '-35%', count: 92, midpoint: -35 },
  { range: '-40%', count: 48, midpoint: -40 },
  { range: '-45%', count: 25, midpoint: -45 },
  { range: '-50%', count: 12, midpoint: -50 },
  { range: '-55%', count: 5, midpoint: -55 },
];

const p95DD = -42;

const tipStyle = {
  background: 'rgba(17,24,39,0.95)',
  border: '1px solid rgba(75,85,99,0.5)',
  borderRadius: 6,
  fontSize: 11,
  fontFamily: 'ui-monospace, monospace',
};

export default function DrawdownDistributionMock() {
  return (
    <div className="platform-mock">
      <div className="platform-mock-header">
        <span className="dot" /> Drawdown Distribution &mdash; Max Drawdowns
      </div>
      <div className="platform-mock-body" style={{ padding: '0.75rem' }}>
        <ResponsiveContainer width="100%" height={150}>
          <BarChart data={bins} margin={{ top: 8, right: 8, bottom: 4, left: 4 }}>
            <XAxis
              dataKey="range"
              tick={{ fill: '#64748b', fontSize: 8 }}
              axisLine={false}
              tickLine={false}
              interval={1}
            />
            <YAxis
              tick={{ fill: '#64748b', fontSize: 8 }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              contentStyle={tipStyle}
              formatter={(v: number) => [v, 'Simulations']}
              labelFormatter={(l) => `Max DD: ${l}`}
              cursor={{ fill: 'rgba(255,255,255,0.05)' }}
            />
            <ReferenceLine
              x="-20%"
              stroke="#3b82f6"
              strokeWidth={1.5}
              strokeDasharray="4 3"
              label={{ value: 'Backtest: -18%', fill: '#3b82f6', fontSize: 9, position: 'top' }}
            />
            <ReferenceLine
              x="-40%"
              stroke="#ef4444"
              strokeWidth={1.5}
              strokeDasharray="4 3"
              label={{ value: `P95: ${p95DD}%`, fill: '#ef4444', fontSize: 9, position: 'insideTopRight' }}
            />
            <Bar dataKey="count" radius={[2, 2, 0, 0]} isAnimationActive={false}>
              {bins.map((bin) => (
                <Cell
                  key={bin.range}
                  fill={bin.midpoint <= p95DD ? '#ef444430' : '#f59e0b18'}
                  stroke={bin.midpoint <= p95DD ? '#ef444466' : '#f59e0b44'}
                  strokeWidth={0.5}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>

        <div style={{
          display: 'flex', justifyContent: 'center', gap: '1.5rem', marginTop: '0.25rem',
          fontSize: '0.5625rem', color: '#94a3b8',
        }}>
          <span>Avg DD: <strong style={{ color: '#f59e0b' }}>-22.4%</strong></span>
          <span>P95 DD: <strong style={{ color: '#ef4444' }}>-42%</strong></span>
          <span>Backtest DD: <strong style={{ color: '#3b82f6' }}>-18%</strong></span>
        </div>
      </div>
      <div className="platform-mock-caption">
        The backtest drawdown (-18%) was luckier than average. Size for the P95 scenario (-42%).
      </div>
    </div>
  );
}
